import { useQueryClient } from '@tanstack/react-query';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { QUERY_KEYS } from 'services/api/constants';
import type { EPDDefinitionModel } from 'types/epd-definition/EPDDefinitionModel';
import WizardHTMLEditor from 'components/v2/epd-wizard-inputs/WizardHTMLEditor';
import WizardNumberInput from 'components/v2/epd-wizard-inputs/WizardNumberInput';
import { toaster } from 'components/v2/toast';
import { useUpdateEpdEstimatedServiceLife } from 'services/api/mutations';
import { useEPDDefinitionInformation } from 'services/api/queries';
import { FieldPanelFullWidth, FieldPanelHeader } from 'styles/v2/Styles.styled';
import { ESTIMATED_SERVICE_LIFE_PROPERTY, hasEstimatedServiceLifeYears } from './estimatedServiceLifeRules';

const YEARS_INPUT_NAME = 'estimatedServiceLifeYears';
const DESCRIPTION_INPUT_NAME = 'estimatedServiceLifeDescription';
const MAX_YEARS = 9999.99;
const MAX_DESCRIPTION_LENGTH = 7000;

interface Props {
  epdId: string;
  epdVersionId: string;
  errors: any;
}

const EstimatedServiceLifeSection: React.FC<Props> = ({ epdId, epdVersionId, errors }) => {
  const epdDefinitionInformation = useEPDDefinitionInformation(epdVersionId).data;
  const updateEstimatedServiceLife = useUpdateEpdEstimatedServiceLife(epdVersionId);
  const { t } = useTranslation();

  const queryClient = useQueryClient();

  // One save of the whole object per change, built from the freshest cached definition so that a change of one
  // field never sends a stale value of the other. When the years are cleared the description is sent as null
  // (the backend and the optimistic update clear it too).
  const save = (change: 'years' | 'description', newValue: number | string | null) => {
    const current = queryClient.getQueryData<EPDDefinitionModel>([QUERY_KEYS.EPD_DEFINITION, epdVersionId])
      ?.estimatedServiceLife ?? { years: null, description: null };
    const value =
      change === 'years'
        ? { years: newValue as number | null, description: newValue === null ? null : current.description }
        : { years: current.years, description: newValue === null ? null : String(newValue) };
    updateEstimatedServiceLife.mutate({
      epdId,
      versionId: epdVersionId,
      propertyName: ESTIMATED_SERVICE_LIFE_PROPERTY,
      newValue: value,
    });
  };

  return (
    <FieldPanelFullWidth>
      <FieldPanelHeader>{t('epdWizard.wizardStep2.properties.estimatedServiceLife.sectionTitle')}</FieldPanelHeader>
      <WizardNumberInput
        label={t('epdWizard.wizardStep2.properties.estimatedServiceLife.title')}
        tooltip={t('epdWizard.wizardStep2.properties.estimatedServiceLife.tooltip')}
        name={YEARS_INPUT_NAME}
        placeholder={t('placeholder.typeHere')}
        defaultValue={epdDefinitionInformation?.estimatedServiceLife?.years ?? null}
        onChanged={(_name, newValue) => save('years', newValue)}
        error={errors?.estimatedServiceLife?.years}
        min={0}
        max={MAX_YEARS}
        maxFractionDigits={2}
        rejectOutOfRange
      />
      {hasEstimatedServiceLifeYears(epdDefinitionInformation?.estimatedServiceLife) && (
        <WizardHTMLEditor
          name={DESCRIPTION_INPUT_NAME}
          label={t('epdWizard.wizardStep2.properties.estimatedServiceLifeDescription.title')}
          tooltip={t('epdWizard.wizardStep2.properties.estimatedServiceLifeDescription.tooltip')}
          value={epdDefinitionInformation?.estimatedServiceLife?.description}
          onChanged={(_name: string, newValue: string | null) => save('description', newValue)}
          stripPastedStyles={true}
          required
          placeholder={t('placeholder.typeHere')}
          maxLength={MAX_DESCRIPTION_LENGTH}
          onMaxLengthExceeded={() =>
            toaster({
              severity: 'error',
              summary: t('epdWizard.wizardStep2.properties.estimatedServiceLifeDescription.maxLengthExceeded.summary'),
              details: t('epdWizard.wizardStep2.properties.estimatedServiceLifeDescription.maxLengthExceeded.details'),
            })
          }
          error={errors?.estimatedServiceLife?.description}
        />
      )}
    </FieldPanelFullWidth>
  );
};

export default EstimatedServiceLifeSection;
